import { LogOut, Settings, UserRound } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { guestSetupPath } from "../authReturn";
import { useAppStore } from "../state";
import { supabase } from "../supabase";
import { getUserDisplayName, useAuthSession } from "../useAuthSession";
import { LoadingButton } from "./LoadingButton";

interface AccountMenuProps {
  onNavigate?: () => void;
}

export function AccountMenu({ onNavigate }: AccountMenuProps) {
  const navigate = useNavigate();
  const { session, loading } = useAuthSession();
  const guest = useAppStore((state) => state.guest);
  const [signingOut, setSigningOut] = useState(false);

  if (loading)
    return (
      <span className="account-skeleton">
        <span className="sr-only">Checking account</span>
      </span>
    );
  if (!session) {
    return (
      <span className="nav-auth-actions">
        <Link className="nav-auth-link" to="/sign-in" onClick={onNavigate}>
          Sign in
        </Link>
        <Link
          className="nav-guest-link"
          to={guest ? "/app" : guestSetupPath("/app")}
          onClick={onNavigate}
        >
          {guest ? "Continue as guest" : "Play anonymously"}
        </Link>
        {guest && (
          <Link className="nav-guest-link" to="/settings" onClick={onNavigate}>
            Guest settings
          </Link>
        )}
      </span>
    );
  }

  const name = getUserDisplayName(session.user);
  const signOut = async () => {
    if (!supabase) return;
    setSigningOut(true);
    await supabase.auth.signOut();
    setSigningOut(false);
    onNavigate?.();
    navigate("/");
  };

  return (
    <details className="account-menu">
      <summary>
        <span className="account-avatar">{name.slice(0, 1).toUpperCase()}</span>
        <span className="account-summary-copy">
          <strong>{name}</strong>
          <small>{session.user.email}</small>
        </span>
      </summary>
      <div className="account-popover">
        <div className="account-popover-head">
          <UserRound />
          <div>
            <strong>{name}</strong>
            <small>{session.user.email}</small>
          </div>
        </div>
        <Link to="/settings" onClick={onNavigate}>
          <Settings /> Account settings
        </Link>
        <LoadingButton
          type="button"
          onClick={() => void signOut()}
          loading={signingOut}
          loadingLabel="Signing out…"
        >
          <LogOut /> Sign out
        </LoadingButton>
      </div>
    </details>
  );
}
